import { createClient } from '@supabase/supabase-js';
import { processLock } from '@supabase/auth-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://gfjznrkxjbskijwurisd.supabase.co';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'sb_publishable_IFQGR5qUfpAXc1sME7qAwA_WFaXHnvx';

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,
    // Every single Supabase query — not just explicit auth calls — goes
    // through auth.getSession() to attach its access token
    // (SupabaseClient#_getAccessToken), and getSession() itself acquires
    // this lock before touching the session. This dashboard fires bursts
    // of concurrent queries constantly (patients+wellness+pending+
    // workflows+vets on load, ~7 queries at once when a patient panel
    // opens) — a FULL bypass (previously `(_name, _t, fn) => fn()`) meant
    // any two of those firing while the token was near expiry could each
    // independently decide to refresh it at the same moment. Supabase
    // rotates refresh tokens on use, so of two concurrent refreshes one
    // gets invalid_grant — this was the actual cause of "have to refresh
    // the page to see data on other tabs" / patient-panel sections hanging
    // (2026-08-14 report), not flakiness in any one query. navigator.locks
    // (the SDK default) was bypassed earlier because a stalled holder
    // could leave the OS-level lock permanently stuck, hanging every
    // subsequent caller forever. processLock (also shipped by
    // @supabase/auth-js) is the fix for that specific problem, not a
    // reason to drop locking altogether: a pure in-memory, single-tab
    // mutex — no navigator.locks, so it can't be orphaned across tabs/
    // reloads (a page reload just resets its in-memory queue) — but it
    // still respects lockAcquireTimeout (5s default), so a stuck holder
    // can only block queued callers for that long, not forever, while
    // still actually serializing concurrent token access within this tab.
    //
    // 2026-09-02/03 — a separate dashboard-hang-on-tab-refocus bug got
    // (wrongly) chased here for three rounds: a global.fetch timeout
    // wrapper (twice, once plain, once with a defensive fallback) and a
    // boundedLock wrapping this exact `lock` option's execution side, not
    // just its acquire side. Diagnostic logging proved neither touched
    // the actual mechanism. Real root cause, found by reading
    // GoTrueClient's source directly: it fires a SIGNED_IN
    // notification on ordinary tab refocus (_recoverAndRefresh, not just
    // real logins) while its OWN internal lock is still held, and
    // App.js's onAuthStateChange handler used to await a Supabase query
    // (hydrateProfile) directly inside that notification — a query which,
    // seeing the lock already held, queued behind the very operation it
    // was blocking, via GoTrueClient's private (non-pluggable)
    // pendingInLock reentrancy queue. A genuine deadlock in a plain
    // internal promise chain, not a network call — no fetch timeout or
    // lock wrapper here could ever have reached it. Fixed at the actual
    // source in App.js's onAuthStateChange (hydrateProfile deferred via
    // setTimeout, per Supabase's own documented guidance never to call a
    // Supabase query synchronously inside this callback) — this file
    // stays exactly as it was for the concurrent-refresh-token fix above,
    // nothing more needed here.
    lock: processLock,
  },
});
